import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { ISelectValue } from 'constants/globalTypes'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IAddOperationPlanTab, IOperationPlanState } from 'types/operationPlans.interface'
import { FineTuningBlock } from '../AddPlan.styled'
import { workAheadOptions } from '../planWording'

export const Horizon = ({ setStep, setState, state }: IAddOperationPlanTab) => {

    const handleNext = () => {
        if (!state.horizon?.horizonType?.value && !state.horizon?.horizonValue && !state.horizon?.schedulerRunFreqMin && !state.horizon?.replenishThreshold && !state.horizon?.maxQueueSize) {
            setStep(6)
        } else if (checkIfAnyHorizonValueIsFilled && (!state.horizon?.schedulerRunFreqMin || !state.horizon?.replenishThreshold || !state.horizon?.maxQueueSize || !state.horizon?.horizonType?.value || !state.horizon?.horizonValue)) {
            toast.warn('Either all or none of the horizon values must be filled')
            return
        } else {
            setStep(6)
        }
    }

    const handleSelect = (v: ISelectValue, n: string) => {
        setState((prev: IOperationPlanState) => ({ ...prev, horizon: { ...prev.horizon, [n]: v } }))
    }

    const handleBack = () => setStep(4)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, valueAsNumber } = e.target
        setState((prev: IOperationPlanState) => ({ ...prev, horizon: { ...prev.horizon, [name]: valueAsNumber } }))
    }

    const checkIfAnyHorizonValueIsFilled = useMemo(() => {
        return Object.values(state.horizon).some((value) => value != null)
    }, [state.horizon])

    const [showAdvanced, setShowAdvanced] = useState(false)

    useEffect(() => {
        if (checkIfAnyHorizonValueIsFilled) {
            setShowAdvanced(true)
        }
    }, [checkIfAnyHorizonValueIsFilled])

    const aheadLabel = state.horizon?.horizonType?.value === 2 ? 'Shifts ahead' : 'Hours ahead'

    return (
        <>
            <Container>
                <ContainerHeader>
                    <TitleText>How the work flows</TitleText>
                </ContainerHeader>
                <ContainerContent>
                    <BodyText grey style={{ marginBottom: '16px' }} >The system keeps a short queue of upcoming work topped up automatically. These settings control the flow — the defaults work for most plans.</BodyText>
                    <Flex column gap='1rem' >
                        <Flex column width='100%'>
                            <HonestSelectInput
                                options={workAheadOptions}
                                labelText='How far ahead to prepare work'
                                name='horizonType'
                                onChange={handleSelect}
                                defaultValue={state.horizon?.horizonType?.value}
                                invalid={checkIfAnyHorizonValueIsFilled && !state.horizon?.horizonType?.value}
                                required={checkIfAnyHorizonValueIsFilled}
                            />
                            <OtherText grey>Leave everything on this step empty to use the defaults</OtherText>
                        </Flex>
                        <Flex width='100%'>
                            <Button variant='black' onClick={() => setShowAdvanced((prev) => !prev)} >{showAdvanced ? 'Hide fine-tuning' : 'Fine-tuning (optional)'}</Button>
                        </Flex>
                        {showAdvanced && <FineTuningBlock>
                            <Flex gap='1rem' width='100%'>
                                <Flex column width='100%'>
                                    <Input
                                        labelText={aheadLabel}
                                        name='horizonValue'
                                        value={state.horizon?.horizonValue}
                                        onChange={handleChange}
                                        invalid={checkIfAnyHorizonValueIsFilled && !state.horizon?.horizonValue}
                                        required={checkIfAnyHorizonValueIsFilled}
                                        type='number'
                                        allowZero
                                        placeholder='8'
                                    />
                                    <OtherText grey>How far ahead the queue is filled</OtherText>
                                </Flex>
                                <Flex column width='100%'>
                                    <Input
                                        labelText='How often to check (minutes)'
                                        name='schedulerRunFreqMin'
                                        value={state.horizon?.schedulerRunFreqMin}
                                        onChange={handleChange}
                                        invalid={checkIfAnyHorizonValueIsFilled && !state.horizon?.schedulerRunFreqMin}
                                        required={checkIfAnyHorizonValueIsFilled}
                                        type='number'
                                        allowZero
                                        placeholder='15...'
                                    />
                                    <OtherText grey>The system re-checks the queue on this rhythm</OtherText>
                                </Flex>
                                <Flex column width='100%'>
                                    <Input
                                        labelText='Top up when fewer than N queued'
                                        name='replenishThreshold'
                                        value={state.horizon?.replenishThreshold}
                                        onChange={handleChange}
                                        invalid={checkIfAnyHorizonValueIsFilled && !state.horizon?.replenishThreshold}
                                        required={checkIfAnyHorizonValueIsFilled}
                                        type='number'
                                        allowZero
                                        placeholder='4'
                                    />
                                    <OtherText grey>Adds more work when the queue runs low</OtherText>
                                </Flex>
                                <Flex column width='100%'>
                                    <Input
                                        labelText='Never queue more than N'
                                        name='maxQueueSize'
                                        value={state.horizon?.maxQueueSize}
                                        onChange={handleChange}
                                        invalid={checkIfAnyHorizonValueIsFilled && !state.horizon?.maxQueueSize}
                                        required={checkIfAnyHorizonValueIsFilled}
                                        type='number'
                                        allowZero
                                        placeholder='10'
                                    />
                                    <OtherText grey>Hard cap on queued work</OtherText>
                                </Flex>
                            </Flex>
                        </FineTuningBlock>}
                    </Flex>
                </ContainerContent>
            </Container>
            <Flex gap='1rem' mt='32' justend>
                <Button variant='secondary' onClick={handleBack}>Back</Button>
                <Button variant='primary' onClick={handleNext}>
                    Next
                </Button>
            </Flex>
        </>
    )
}
